import { t } from './i18n';
/**
 * buildCityReplayTimeline(data, routeItems, cityName)
 *
 * Retorna eventos de replay ordenados cronologicamente para uma cidade.
 * Cada evento tem { date, type, data }.
 *   type: 'checkin' | 'route' | 'diary' | 'concert' | 'photo'
 * Eventos sem data são ignorados. Cidade sem dados retorna [].
 */

function extractDateStr(item) {
  if (item.date) return String(item.date).slice(0, 10);
  if (item.entry_date) return String(item.entry_date).slice(0, 10);
  if (item.event_date) return String(item.event_date).slice(0, 10);
  if (item.photo_date) return String(item.photo_date).slice(0, 10);
  return null;
}

function isCityMatch(item, cityName) {
  const city = (item.city || '').toLowerCase();
  const target = (cityName || '').toLowerCase();
  return city === target;
}

export function buildCityReplayTimeline(data = {}, routeItems = [], cityName) {
  if (!cityName) return [];

  const events = [];

  (data.checkins || []).forEach(item => {
    if (!isCityMatch(item, cityName)) return;
    const date = extractDateStr(item);
    if (!date) return;
    events.push({ date, type: 'checkin', data: item });
  });

  (data.diary || []).forEach(item => {
    const date = extractDateStr(item);
    if (!date) return;
    events.push({ date, type: 'diary', data: item });
  });

  (data.concerts || []).forEach(item => {
    if (!isCityMatch(item, cityName)) return;
    const date = extractDateStr(item);
    if (!date) return;
    events.push({ date, type: 'concert', data: item });
  });

  (data.photos || []).forEach(item => {
    const date = extractDateStr(item);
    if (!date) return;
    events.push({ date, type: 'photo', data: item });
  });

  (routeItems || []).forEach(route => {
    if (!route || !route.date) return;
    const points = route.points || route.route;
    if (!points || !Array.isArray(points) || points.length === 0) return;
    events.push({ date: route.date, type: 'route', data: { ...route, points } });
  });

  events.sort((a, b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type));

  return events;
}

const TILE_SIZE = 256;

function lonToTileX(lon, zoom) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
}

function latToTileY(lat, zoom) {
  const latRad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, zoom),
  );
}

export function loadTileImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Falha ao carregar tile: ${url}`));
    img.src = url;
  });
}

function tileUrl(x, y, z) {
  const s = ['a', 'b', 'c', 'd'][Math.abs(x + y) % 4];
  return `https://${s}.basemaps.cartocdn.com/dark_all/${z}/${x}/${y}.png`;
}

function latLonToPixel(lat, lon, zoom) {
  const x = (lon + 180) / 360 * Math.pow(2, zoom) * TILE_SIZE;
  const latRad = (lat * Math.PI) / 180;
  const y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom) * TILE_SIZE;
  return { x, y };
}

async function drawTilesOnCanvas(canvas, ctx, bounds, zoom) {
  const minTileX = lonToTileX(bounds.getWest(), zoom);
  const maxTileX = lonToTileX(bounds.getEast(), zoom);
  const minTileY = latToTileY(bounds.getNorth(), zoom);
  const maxTileY = latToTileY(bounds.getSouth(), zoom);

  const topLeftPixel = latLonToPixel(bounds.getNorth(), bounds.getWest(), zoom);
  const tileOriginX = lonToTileX(bounds.getWest(), zoom) * TILE_SIZE;
  const tileOriginY = latToTileY(bounds.getNorth(), zoom) * TILE_SIZE;
  const offsetX = tileOriginX - topLeftPixel.x;
  const offsetY = tileOriginY - topLeftPixel.y;

  const loadPromises = [];
  const tiles = [];

  for (let tx = minTileX; tx <= maxTileX; tx++) {
    for (let ty = minTileY; ty <= maxTileY; ty++) {
      const url = tileUrl(tx, ty, zoom);
      const drawX = (tx - minTileX) * TILE_SIZE + offsetX;
      const drawY = (ty - minTileY) * TILE_SIZE + offsetY;
      loadPromises.push(
        loadTileImage(url).then(img => {
          tiles.push({ img, x: drawX, y: drawY });
        }).catch(() => {}),
      );
    }
  }

  await Promise.all(loadPromises);
  tiles.forEach(({ img, x, y }) => {
    ctx.drawImage(img, x, y, TILE_SIZE, TILE_SIZE);
  });
}

export async function exportReplayVideo(mapElement, replayLayer, durationMs, onProgress) {
  if (!mapElement) throw new Error(t('web.cityReplay.mapMissing'));
  if (!window.MediaRecorder) throw new Error(t('web.cityReplay.mediaUnsupported'));

  const map = mapElement._leaflet_map || mapElement._leaflet_id;
  const leafletMap = (map && map._container && map._container._leaflet_map) ? null : null;

  const rect = mapElement.getBoundingClientRect();
  const width = Math.floor(rect.width);
  const height = Math.floor(rect.height);

  if (width === 0 || height === 0) {
    throw new Error(t('web.cityReplay.mapInvisible'));
  }

  const zoom = 13;
  const bounds = leafletMap ? leafletMap.getBounds() : null;
  const mapBounds = bounds || { getWest: () => -46.8, getEast: () => -46.4, getNorth: () => -23.4, getSouth: () => -23.7 };

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  await drawTilesOnCanvas(canvas, ctx, mapBounds, zoom);

  const stream = canvas.captureStream(30);
  const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : 'video/webm';

  const chunks = [];
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2500000 });

  return new Promise((resolve, reject) => {
    recorder.ondataavailable = e => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      resolve(blob);
    };

    recorder.onerror = () => {
      reject(new Error(t('web.cityReplay.recordError')));
    };

    recorder.start();

    let frameCount = 0;
    const totalFrames = Math.floor(durationMs / 33);

    const drawFrame = () => {
      if (frameCount >= totalFrames) {
        recorder.stop();
        return;
      }

      if (replayLayer && replayLayer.getLayers) {
        replayLayer.getLayers().forEach(layer => {
          if (layer.getLatLng) {
            const latlng = layer.getLatLng();
            const point = leafletMap ? leafletMap.latLngToContainerPoint(latlng) : null;
            if (point) {
              ctx.beginPath();
              ctx.arc(point.x, point.y, 6, 0, 2 * Math.PI);
              ctx.fillStyle = '#3498db';
              ctx.fill();
            }
          }
        });
      }

      frameCount++;
      if (onProgress) onProgress(Math.floor((frameCount / totalFrames) * 100));
      requestAnimationFrame(drawFrame);
    };

    drawFrame();
  });
}
